import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';

import {
  ConfirmDeletingElement,
  CustomInputNumber,
  DurationSelects,
  EmptyComponentWithDataTestId,
  FastCreationModal,
} from '@app/components';
import { CustomAlert } from '@app/components/CustomAlert';
import { OFF, ONE_TIME_CODE } from '@app/constants';
import { ERROR, ERROR_REQUIRED_FIELD } from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import { useClientServiceParams } from '@pages/Clients/EditClientService/components/ClientServiceParams/hooks/useClientServiceParams';

import '@pages/Clients/EditClientService/components/ClientServiceParams/styles/ClientServiceParams.desktop.scss';
import '@pages/Clients/EditClientService/components/ClientServiceParams/styles/ClientServiceParams.tablet.scss';

const { Text } = Typography;
const { Item } = Form;
const { TextArea } = Input;

export const ClientServiceParams = () => {
  const {
    form,
    clientId,
    isLoader,
    serverError,
    isRange,
    categories,
    categoriesLoader,
    modalForm,
    isCreateModalOpen,
    setIsCreateModalOpen,
    setRange,
    sendDataToDb,
    setServerError,
    sendNewCategoryToDb,
    initialValues,
    isCategorySelect,
    setCategorySelect,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleDeleteClientService,
    hasDeleteServiceRight,
    hasCreateServiceCategoryRight,
    isEditMode,
    isArchived,
    isRestoring,
    hasEditServiceRight,
    handleRestoreClientService,
  } = useClientServiceParams();

  if (!form.getFieldsValue() && clientId) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}client-service-params`}>
      <Form
        className="bc-form"
        form={form}
        name="basic"
        style={{ maxWidth: 800, width: '100%' }}
        onFinish={sendDataToDb}
        autoComplete={OFF}
        initialValues={initialValues}
      >
        <Space
          direction="vertical"
          size="middle"
          className="form-space"
          style={{ width: '100%' }}
        >
          <div className="field-container">
            <Text className="label required" strong data-testid="service-name">
              Название
            </Text>
            <Item
              name="Name"
              className="mg-0"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
              ]}
            >
              <Input
                data-testid="service-name-input"
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={200}
              />
            </Item>
          </div>
          <div className="field-container">
            <Text className="label" strong data-testid="service-name">
              Публичное название
            </Text>
            <Item name="PublicName" className="mg-0">
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={200}
              />
            </Item>
          </div>

          <div className="field-container">
            <Text className="label required" strong data-testid="category">
              Категория
            </Text>
            <Item
              name="Category"
              className="mg-0"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
              ]}
            >
              <Select
                notFoundContent={<EmptyComponentWithDataTestId />}
                data-testid="category-select"
                className="field-selector"
                open={isCategorySelect}
                size="large"
                options={categories}
                loading={categoriesLoader}
                popupRender={(menu) => (
                  <>
                    {hasCreateServiceCategoryRight && (
                      <>
                        <Space style={{ padding: '0 8px 4px' }}>
                          <Button
                            type="link"
                            style={{ padding: 0 }}
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setCategorySelect(false);
                              modalForm.setFieldValue('Name', '');
                              modalForm.setFieldValue('Description', '');
                              setIsCreateModalOpen(true);
                            }}
                            data-testid="create-category"
                          >
                            Создать категорию
                          </Button>
                        </Space>
                        <Divider style={{ margin: '8px 0' }} />
                      </>
                    )}
                    {menu}
                  </>
                )}
                onOpenChange={setCategorySelect}
              />
            </Item>
          </div>

          <div className="field-container">
            <Text className="label" strong data-testid="medical-service-code">
              Код по номенклатуре МЗ РФ
            </Text>
            <Item
              name="MedicalServiceCode"
              className="mg-0"
              rules={[
                {
                  pattern: /^[a-zA-Zа-яА-Я0-9.]+$/,
                  message: 'Можно вводить только буквы, цифры и точку',
                },
              ]}
            >
              <Input size="large" maxLength={14} />
            </Item>
          </div>

          <div className="price-container field-container">
            <div className="price-container__title">
              <Text className="label required" strong data-testid="price">
                Цена
              </Text>
            </div>
            <div className="price">
              <Space direction="vertical">
                <Space className="prices-tablet">
                  <Item
                    name="PriceS"
                    style={{ margin: 0, width: isRange ? 'auto' : 400 }}
                    rules={[
                      {
                        required: true,
                        message: generateSpanWithDataTestId(
                          ERROR_REQUIRED_FIELD,
                          'alert'
                        ),
                      },
                    ]}
                  >
                    <CustomInputNumber
                      inputValue={form.getFieldValue('PriceS')}
                      style={{ height: '38px' }}
                      className="custom-amount"
                      onSuccess={({ value }) => {
                        form.setFieldValue('PriceS', value);
                      }}
                      addonBefore="От"
                      dataTestId="price-from"
                    />
                  </Item>
                  {isRange && (
                    <Item
                      name="PriceE"
                      className="mg-0"
                      rules={[
                        {
                          required: true,
                          message: generateSpanWithDataTestId(
                            ERROR_REQUIRED_FIELD,
                            'alert'
                          ),
                        },
                      ]}
                    >
                      <CustomInputNumber
                        inputValue={form.getFieldValue('PriceE')}
                        style={{ height: '38px' }}
                        className="custom-amount"
                        onSuccess={({ value }) => {
                          form.setFieldValue('PriceE', value);
                        }}
                        addonBefore="До"
                        dataTestId="price-to"
                      />
                    </Item>
                  )}
                </Space>
                <Item name="Range" className="mg-0">
                  <Checkbox
                    checked={isRange}
                    onChange={() => {
                      isRange && form.setFieldValue('PriceE', '');
                      setRange((isRange) => !isRange);
                    }}
                    data-testid="price-range-checkbox"
                  >
                    {generateSpanWithDataTestId(
                      'Указать цену в диапазоне',
                      'price-range'
                    )}
                  </Checkbox>
                </Item>
              </Space>
            </div>
          </div>

          <div className="field-container">
            <Text className="label" strong data-testid="duration">
              Длительность
            </Text>
            <Item name="Duration" className="mg-0">
              <DurationSelects className="duration-select" />
            </Item>
          </div>

          <div className="field-container">
            <Text className="label" strong data-testid="description">
              Описание
            </Text>

            <Item name="Description" className="mg-0">
              <TextArea
                className="description-textarea"
                rows={3}
                maxLength={4000}
              />
            </Item>
          </div>
        </Space>
      </Form>
      {/* {TODO Поступило требование временно убрать} */}
      {/* <ClientServiceAdditions
        isLoader={isLoader}
        additionsForm={additionsForm}
        allAdditions={allAdditions}
        unselectedAdditions={unselectedAdditions}
        selectedAdditions={selectedAdditions}
        setSelectedAdditions={setSelectedAdditions}
        setUnselectedAdditions={setUnselectedAdditions}
      /> */}
      <Flex
        gap={16}
        justify="space-between"
        className="action-button-container"
      >
        {serverError && (
          <CustomAlert
            errorType={ERROR}
            serverError={serverError}
            setServerError={setServerError}
          />
        )}

        <Button
          size="large"
          className="save-button"
          type="primary"
          loading={isLoader}
          icon={<SaveOutlined />}
          onClick={form.submit}
          data-testid="save"
        >
          Сохранить
        </Button>

        {isEditMode &&
          (isArchived
            ? hasEditServiceRight && (
                <Button
                  size="large"
                  className="save-button"
                  disabled={isLoader}
                  loading={isRestoring}
                  icon={<RollbackOutlined />}
                  onClick={handleRestoreClientService}
                  data-testid="restore-service"
                >
                  Вернуть из архива
                </Button>
              )
            : hasDeleteServiceRight &&
              isEditMode && (
                <Button
                  danger
                  size="large"
                  className="save-button"
                  disabled={isLoader}
                  icon={<DeleteOutlined />}
                  onClick={() => setIsDeleteModalOpen(true)}
                  data-testid="cancel"
                >
                  Удалить
                </Button>
              ))}
      </Flex>
      <FastCreationModal
        title="Создание категории"
        isModalOpen={isCreateModalOpen}
        handleOk={sendNewCategoryToDb}
        handleCancel={() => setIsCreateModalOpen(false)}
        modalForm={modalForm}
        finishModal={sendNewCategoryToDb}
        nameMaxLength={100}
        descriptionMaxLength={400}
        titleDataTestId="create-category-title"
      />
      <ConfirmDeletingElement
        modalOpen={isDeleteModalOpen}
        setModalOpen={setIsDeleteModalOpen}
        handleDelete={handleDeleteClientService}
        content="Вы уверены, что хотите удалить услугу?"
        title="Удаление услуги"
        titleDataTestId="delete-services-title"
        descriptionDataTestId="delete-services-description"
      />
    </div>
  );
};
